import React from 'react';
import { LogOut, LineChart, Globe, Clock, Tag, Database, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { Link, useLocation } from 'react-router-dom';

const SidebarLink: React.FC<{ 
  label: string; 
  to: string; 
  icon: React.ReactNode; 
  collapsed?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}> = ({ label, to, icon, collapsed, disabled = false, disabledTooltip }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  if (disabled) {
    return (
      <div
        title={disabledTooltip || label}
        className={`flex items-center gap-3 px-4 py-2 text-sm text-gray-400 cursor-not-allowed opacity-60 ${collapsed ? 'justify-center' : ''}`}
      >
        <Lock size={20} className="text-gray-400" />
        {!collapsed && <span>{label}</span>}
      </div>
    );
  }

  return (
    <Link
      to={to}
      title={label}
      className={`flex items-center gap-3 px-4 py-2 text-sm ${
        isActive 
          ? 'text-blue-600 font-medium' 
          : 'text-gray-700 hover:text-gray-900'
      } ${collapsed ? 'justify-center' : ''}`}
    >
      {icon}
      {!collapsed && <span>{label}</span>}
    </Link>
  );
};

const Sidebar: React.FC<{ collapsed?: boolean; onToggle?: () => void }> = ({ collapsed = false, onToggle }) => {
  const { logout, user, isAuthenticated } = useAuth();
  const { activeDatabase } = useDatabaseContext();
  const handleToggle = onToggle || (() => {});

  const hasActiveDatabase = !!activeDatabase;

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className={`${collapsed ? 'w-16' : 'w-64'} bg-white h-screen shadow-lg flex flex-col fixed left-0 top-0 z-10`}>
      {/* Header */}
      <div className={`flex items-center h-16 flex-shrink-0 px-4 ${collapsed ? 'justify-center' : ''}`}>
        <Link to="/" title="Accueil" className="flex items-center">
          <img
            className="h-8 w-auto"
            src="/logo192.png"
            alt="Isiforecast"
          />
          {!collapsed && (
            <span className="ml-2 text-gray-900 text-lg font-medium">
              ISIFORECAST
            </span>
          )}
        </Link>
      </div>

      {/* Navigation - Takes up remaining space */}
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        <button 
          onClick={handleToggle}
          className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 w-full ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Déplier' : 'Réduire'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!collapsed && 'Réduire'}
        </button>
        <SidebarLink 
          label="Importer des données" 
          to="/mes-donnees" 
          icon={<Database size={20} className="text-gray-500" />} 
          collapsed={collapsed}
        />
        {/* <SidebarLink 
          label="Dashboard" 
          to="/" 
          icon={<LayoutDashboard size={20} className="text-gray-500" />}  
        /> */}
        <SidebarLink 
          label="Mes données" 
          to="/commandes" 
          icon={<Globe size={20} className="text-gray-500" />} 
          collapsed={collapsed}
          disabled={!hasActiveDatabase}
          disabledTooltip="Sélectionnez d'abord une base de données depuis 'Importer des données'"
        />
        <SidebarLink 
          label="Prévision" 
          to="/previsions" 
          icon={<LineChart size={20} className="text-gray-500" />} 
          collapsed={collapsed}
          disabled={!hasActiveDatabase}
          disabledTooltip="Sélectionnez d'abord une base de données depuis 'Importer des données'"
        />
        <SidebarLink 
          label="Cycles de vie" 
          to="/cycles-de-vie" 
          icon={<Clock size={20} className="text-gray-500" />} 
          collapsed={collapsed}
          disabled={!hasActiveDatabase}
          disabledTooltip="Sélectionnez d'abord une base de données depuis 'Importer des données'"
        />
        {/* <SidebarLink 
          label="Alertes" 
          to="/alertes" 
          icon={<Bell size={20} className="text-gray-500" />}  
        /> */}
        <SidebarLink 
          label="Promotions" 
          to="/promotions" 
          icon={<Tag size={20} className="text-gray-500" />} 
          collapsed={collapsed}
          disabled={!hasActiveDatabase}
          disabledTooltip="Sélectionnez d'abord une base de données depuis 'Importer des données'"
        />

        {/* Old 'Stocks' page removed; outstock is now in Commandes tab */}
      </nav>

      {/* User section - Fixed at bottom */}
      <div className={`border-t ${collapsed ? 'p-2' : 'p-4'} flex-shrink-0`}>
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} mb-4`}>
          <img 
            src={`https://ui-avatars.com/api/?name=${user?.username || 'User'}&background=random`} 
            alt={user?.username || 'User'} 
            className="w-10 h-10 rounded-full"
          />
          {!collapsed && (
            <div>
              <p className="text-sm font-medium">
                {user?.username || (isAuthenticated ? 'Chargement...' : 'Utilisateur')}
              </p>
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                {user?.is_superuser ? "Admin" : user?.api_access_level || 'User'}
              </span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {/* <button className={`flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 w-full px-3 py-2 rounded-md hover:bg-gray-50 ${collapsed ? 'justify-center' : ''}`}>
            <Settings size={18} /> {!collapsed && 'Paramètres'}
          </button> */}
          <button 
            onClick={handleLogout}
            className={`flex items-center gap-2 text-sm text-red-600 hover:text-red-800 w-full px-3 py-2 rounded-md hover:bg-red-50 ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut size={18} /> {!collapsed && 'Déconnexion'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
